﻿import React, { useState, useEffect } from 'react';
import { KarlFischerTitrationBenchSheet } from '../models/karl-fischer-titration-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, KarlFischerType, NoteType, Entities } from '../models/enums';
import KarlFischerSamples from './KarlFischerSamples';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import BenchSheetData from './BenchSheetData';
import NoteViewer from './NoteViewer';
import { CrudFunctions } from '../utilities/crud-functions';
import DocumentLoader from './DocumentLoader';
import TicketLogEntryForm from './TicketLogEntryForm';
import { FieldUpdate } from '../models/field-update';
import { UserDataStore } from '../store/userDataStore';
import BenchSheetToolbar from './BenchSheetToolbar';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import NumberBox from 'devextreme-react/number-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export default function KarlFischerTitrationBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<KarlFischerTitrationBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    const batchId = benchSheet && benchSheet.batchId ? benchSheet.batchId : undefined;

    return (
        <div className="pw-content">
            <BenchSheetToolbar title="KF-T Bench Sheet"
                subtitle="Karl Fischer Titration"
                auditRowPointer={benchSheetId} entity={Entities.KftBenchSheets}
                technologyId={BenchSheetTechnologyType.KarlFischerTitrimetric}
                documentationId={DocumentationConstants.KFTBenchSheet}
                showItemInformation={showBenchSheetInformationDialog}
                showTicketLog={showTicketLogEntryDialog} />

            <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

            <div>
                <SecondaryBenchSheetInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technology={BenchSheetTechnologyType.KarlFischerTitrimetric}
                    title="KFT Instrument" />
            </div>

            <div className="lq-sub-title pw-blue">Titer Information</div>
            <div className="pw-comp-row pw-margin-bottom-1">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Water Uncertainty (%)</div>
                    <NumberBox id="titerWaterUncertainty" className="textbox"
                        onValueChanged={onTiterWaterUncertaintyValueChanged}
                        placeholder='water uncertainty'
                        value={benchSheet && benchSheet.titerWaterUncertainty ?
                            benchSheet.titerWaterUncertainty : undefined} />
                </div>
            </div>

            <div className="pw-margin-bottom-1">
                <div className="lq-sub-title pw-margin-top-1">Titer Certificate</div>
                <DocumentLoader parentId={benchSheetId}
                    documentType={DocumentType.ProductPlanning}
                    readOnly={!canEdit} />
            </div>

            <div>
                <KarlFischerSamples benchSheetId={benchSheetId}
                    karlFischerType={KarlFischerType.Titration} />
            </div>

            <div>
                <div className="lq-header pw-margin-top-05">
                    <div className="lq-header-title">Standards Documents</div>
                </div>

                {/* Right now just have all standards documents listed under generic StandardsDocument type */}
                <DocumentLoader parentId={batchId} readOnly={true}
                    documentType={DocumentType.StandardsDocument}
                    canDelete={canDelete} />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <NoteViewer height='12rem' title="Bench Sheet Notes" readOnly={!canEdit}
                    noteType={NoteType.BenchSheet} parentId={benchSheetId} />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <BenchSheetData parentId={benchSheetId}
                    readOnly={!canEdit} canDelete={canDelete}
                    technology={BenchSheetTechnologyType.KarlFischerTitrimetric}
                    documentType={DocumentType.General}
                    imageHandlerTitle="Weight Ticket" />
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="KF-T Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    async function onTiterWaterUncertaintyValueChanged(e: any) {
        if (!e.event)
            return;

        let fieldUpdate: FieldUpdate = {
            entity: 'KarlFischerTitrationBenchSheets',
            field: 'titerWaterUncertainty',
            rowId: benchSheetId,
            value: e.value
        };

        setBenchSheet({ ...benchSheet, titerWaterUncertainty: e.value });

        await updateKarlFischerTitrationField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Titer Information");
            });
    }

    // get functions

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function updateKarlFischerTitrationField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/benchsheetfield", fieldUpdate);
    }

    // show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    async function getAllData() {
        await getBenchSheet();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
